// Service hook (TS-2.5.01, Resource Accessor layer). `entryKey` of null means "nothing expanded"
// and skips the fetch. audit.ts's own header: GetEntryAsync 404s uniformly for "does not exist"
// and "exists but out of scope" (D-157) - this hook surfaces that as an ordinary ApiError("notFound",
// ...) and never claims to distinguish the two, since the server itself does not.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { AuditEntryDetail } from "../../../api/generated/contracts";
import { auditApi } from "../../../api/audit";

export interface UseAuditEntryDetailResult {
  readonly detail: AuditEntryDetail | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
}

export function useAuditEntryDetail(entryKey: string | null): UseAuditEntryDetailResult {
  const [detail, setDetail] = useState<AuditEntryDetail | null>(null);
  const [loading, setLoading] = useState(entryKey !== null);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!entryKey) {
      setDetail(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    void auditApi
      .getEntry(entryKey)
      .then((next) => {
        setDetail(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The entry could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [entryKey]);

  useEffect(refresh, [refresh]);

  return { detail, loading, error };
}
